import React from 'react';
import { CALCULATION_ZONE_CONFIG, PRICE_RETURN } from './constants';
import {
  getZoneValue,
  getPreviewLabel,
  shouldRenderPreviewItem,
  buildPreviewDisplayRow,
  getPreviewItemDisplayValue,
} from './index';
import styles from './ShowPreview.module.scss';

const ShowPreview = ({
  data,
  previousData = null,
  withholdingTaxTemplateData,
  isAnnualizedDailyDecrement,
  orgPrefix,
  launchDate,
  automaticallyAdjustDivisor,
  calculationZoneMap,
}) => {
  const getDisplayValue = (item, value) =>
    getPreviewItemDisplayValue(item, value, {
      launchDate,
      orgPrefix,
      withholdingTaxTemplateData,
    });

  const buildCalculationZoneExtraFields = sourceData => ({
    timezone: getZoneValue(calculationZoneMap, sourceData, CALCULATION_ZONE_CONFIG.CALC_TIMEZONE),
    calculation_start_hour: getZoneValue(
      calculationZoneMap,
      sourceData,
      CALCULATION_ZONE_CONFIG.CALC_OPEN_TIME,
    ),
    calculation_end_hour: getZoneValue(
      calculationZoneMap,
      sourceData,
      CALCULATION_ZONE_CONFIG.CALC_CLOSE_TIME,
    ),
    calculation_start_week_day: getZoneValue(
      calculationZoneMap,
      sourceData,
      CALCULATION_ZONE_CONFIG.CALC_START_WEEK_DAY,
      true,
    ),
    calculation_end_week_day: getZoneValue(
      calculationZoneMap,
      sourceData,
      CALCULATION_ZONE_CONFIG.CALC_END_WEEK_DAY,
      true,
    ),
    calculation_eod_time: getZoneValue(
      calculationZoneMap,
      sourceData,
      CALCULATION_ZONE_CONFIG.CALC_EOD_TIME,
    ),
  });

  const isBloombergDissemination = value =>
    typeof value === 'string' && value.toLowerCase().includes('bloomberg');

  const buildBloombergDisseminationExtraFields = sourceData => {
    if (!isBloombergDissemination(sourceData?.dissemination)) return {};

    const setup = sourceData?.bloomberg_dissemination_setup;
    if (!setup) return {};

    return {
      bloomberg_global: setup.global ? 'Yes' : 'No',
      ...(!setup.global && setup.country_code
        ? {
            bloomberg_country_code: setup.country_code,
          }
        : {}),
      bloomberg_geographical_region: setup.geographical_region,
      bloomberg_timezone_of_trading_hours: setup.timezone_of_trading_hours,
      bloomberg_start_time: setup.start_time,
      bloomberg_end_time: setup.end_time,
      bloomberg_trading_days: setup.trading_days,
    };
  };

  const bloombergDisseminationExtraFields = buildBloombergDisseminationExtraFields(data);
  const combinedData = {
    ...data,
    ...buildCalculationZoneExtraFields(data),
  };
  const combinedPreviousData = previousData
    ? {
        ...previousData,
        ...buildCalculationZoneExtraFields(previousData),
        ...buildBloombergDisseminationExtraFields(previousData),
      }
    : null;
  const previewEntries = Object.entries(combinedData ?? {}).flatMap(entry => {
    const [key] = entry;
    if (key !== 'dissemination') return [entry];

    return [entry, ...Object.entries(bloombergDisseminationExtraFields)];
  });

  const displayData = previewEntries
    .filter(([key, value]) => {
      return (
        typeof value === 'string' ||
        typeof value === 'number' ||
        key === 'divisor' ||
        (key === 'withholding_tax_id' && data?.return_type === 'NET_RETURN')
      );
    })
    .map(([item, value]) => {
      if (
        !shouldRenderPreviewItem(
          item,
          data?.return_type,
          automaticallyAdjustDivisor,
          data?.exists_cash_constituent,
          isAnnualizedDailyDecrement,
          data?.divisor_determination_setup,
        )
      )
        return null;

      const label = getPreviewLabel(item);
      const displayValue = getDisplayValue(item, value);
      const previousRawValue = combinedPreviousData ? combinedPreviousData[item] : null;
      const previousDisplayValue =
        combinedPreviousData != null ? getDisplayValue(item, previousRawValue) : null;
      const isBloombergChildRow = item.startsWith('bloomberg_');
      return {
        key: item,
        label,
        value: displayValue,
        previousValue: previousDisplayValue,
        isNestedRow: isBloombergChildRow,
        preserveRawValue: isBloombergChildRow,
      };
    });

  if (!displayData || displayData.length === 0) {
    return <div className={styles.noData}>No data preview available</div>;
  }

  const filteredData = displayData.filter(Boolean);
  const showOldValueColumn = previousData != null;
  const isPriceReturn =
    data?.calculation_type === PRICE_RETURN || data?.return_type === PRICE_RETURN;
  // Price Return baskets do not reinvest dividends, so cash distribution settings do not apply.
  // @see basketManageModule/BUSINESS_RULES.md — section "Price Return does not use Cash Distribution"
  const priceReturnExcludedKeys = new Set(['cash_distribution', 'application_point']);
  const rowsToRender = filteredData
    .filter(row => !isPriceReturn || !priceReturnExcludedKeys.has(row.key))
    .map(row => buildPreviewDisplayRow(row, showOldValueColumn));

  return (
    <div className={styles.container}>
      <table
        className={`${styles.table} ${showOldValueColumn ? styles.comparisonTable : ''}`.trim()}
      >
        <thead className={styles.thead}>
          <tr>
            <th className={styles.th}>
              <div className={styles.thContent}>Property</div>
            </th>
            {showOldValueColumn && (
              <th className={styles.th}>
                <div className={styles.thContent}>Previous Parameters</div>
              </th>
            )}
            <th className={styles.th}>
              <div className={styles.thContent}>
                {showOldValueColumn ? 'Updated Parameters' : 'Value'}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rowsToRender.map((row, idx) => (
            <tr key={row.key || idx} className={styles.tableRow}>
              <td
                className={`${styles.tdLabel} ${
                  row.isNestedRow ? styles.tdLabelNested : ''
                }`.trim()}
              >
                {row.displayLabel}
              </td>
              {showOldValueColumn && <td className={styles.td}>{row.previousDisplayValue}</td>}
              <td className={`${styles.td} ${row.hasChanged ? styles.changed : ''}`.trim()}>
                {showOldValueColumn && !row.hasChanged ? '-' : row.displayValue}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ShowPreview;
